import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';
import { BaseService } from './base.service';


@Injectable({
    providedIn: 'root'
})
export class CommonHubService implements OnDestroy {
    private readonly apiUrl: string;
    apiHeader: {};
    private readonly executionSuccessSubject = new Subject<{ id: string, msg: string }>();
    private static readonly COMMON_HUB_CONNECTION_ENDPOINT = 'commonhub';
    executionSuccess$ = this.executionSuccessSubject.asObservable();
    private readonly activeExecutions = new BehaviorSubject<{ id: string, action: string }[]>([]);
    activeExecutions$ = this.activeExecutions.asObservable();
    private hubConnection!: signalR.HubConnection;
    private isConnectionStarted: boolean = false;
    private eventListenersRegistered: boolean = false;

    constructor(
        private readonly baseService: BaseService,
        private readonly http: HttpClient,
        private readonly authService: AuthService
    ) {
        this.apiUrl = (this.baseService.getCorrectUrl("") + CommonHubService.COMMON_HUB_CONNECTION_ENDPOINT).replace('/api/', '/').replace(/\/$/, ''); // Ensure no trailing slash
        // Initialize headers with default values
        this.apiHeader = this.baseService.getNewOptions().headers;
        const token = this.baseService.getNewOptions().headers['authorization'];
        this.apiHeader['authorization'] = this.getToken();
        console.log('API URL:', this.apiUrl, this.apiHeader, token);
    }

    getToken() {
        const token = this.baseService.getNewOptions().headers['authorization'];
        console.log('getToken called, token:', token);
        return token;
    }

    async startConnection() {
        const token = this.getToken();
        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(`${environment.apiEndpoint}/${CommonHubService.COMMON_HUB_CONNECTION_ENDPOINT}`, {
                transport: signalR.HttpTransportType.LongPolling, // Force HTTP transport so token travels via header, not query string
                skipNegotiation: false,
                accessTokenFactory: () => {

                    const token1 = (this.baseService.getNewOptions().headers['authorization']).replace(
							'Bearer ',
							'',
						);
                    console.log('Access token factory called, token1:', token1);
                    console.log('Access token factory called, token2:', this.baseService.getNewOptions().headers['authorization']);

                    
                    console.log('Access token factory called, token3:', token);
                    return token; // Return the token for authentication 
                }
            })
            .configureLogging(signalR.LogLevel.Information)
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // Customize reconnect delays
            .build();

        this.hubConnection
            .start()
            .then(() => console.log('Connection started'))
            .catch((err) => console.error('Error while starting SignalR connection: ', err));


        this.hubConnection.on('ExecutionCompleted', (id) => {
            this.executionSuccessSubject.next({ id, msg: 'ExecutionCompleted' });  // Notify success with ID
            this.removeExecution(id);
        });
        this.hubConnection.on('ExecutionStopped', (id) => {
            this.executionSuccessSubject.next({ id, msg: 'ExecutionStopped' }); // Notify success with ID
            this.removeExecution(id);
        });
        this.hubConnection.on('ExecutionFailed', (id, message) => {
            this.executionSuccessSubject.next({ id, msg: 'ExecutionFailed' }); // Notify failure with ID
            this.removeExecution(id);
        });

        // Listen for Execution Started events
        this.hubConnection.on('ExecutionStarted', (id: string, action: string) => {
            this.executionSuccessSubject.next({ id, msg: 'ExecutionStarted' }); // Notify success with ID
            this.updateActiveExecutions({ id, action });
        });
    }

    getRunningStatus(): boolean {
        return this.hubConnection?.state === signalR.HubConnectionState.Connected;
    }

    waitForConnection(): Promise<void> {
        return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                if (this.getRunningStatus()) {
                    clearInterval(interval);
                    resolve();
                }
            }, 1000);
            // Optional: Add a timeout to reject the promise if the connection is not established within a certain time
            setTimeout(() => {
                clearInterval(interval);
                reject(new Error('Failed to connect within the timeout period.'));
            }, 30000); // 30 seconds timeout
        });
    }

    async main() {
        try {
            await this.waitForConnection();
            // Place the code you want to run after the connection is established here
        } catch (error) {
            console.error('Error:', error);
        }
    }

    async getRunningExecutions(): Promise<string[]> {
        // Wait for connection to be established
        if (this.hubConnection?.state !== signalR.HubConnectionState.Connected) {
            try {
                await this.waitForConnection();
            } catch (error) {
                console.error('Error waiting for connection:', error);
                return [];
            }
        }
        
        return this.hubConnection.invoke('GetRunningExecutions', this.apiHeader['authorization'])
            .then((executions: string[]) => {
                return executions;
            })
            .catch((err) => {
                console.error('Error fetching executions:', err);
                return [];
            });
    }

    getRunningExecutionsWithRetry(retries: number = 5, delay: number = 2000): Promise<string[]> {
        return new Promise((resolve, reject) => {
            const attempt = (remainingRetries: number) => {
                if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
                    this.hubConnection.invoke('GetRunningExecutions', this.apiHeader['authorization'])
                        .then((executions: string[]) => {
                            resolve(executions);
                        })
                        .catch((err) => {
                            console.error('Error fetching executions:', err);
                            reject(err);
                        });
                } else if (remainingRetries > 0) {
                    setTimeout(() => attempt(remainingRetries - 1), delay);
                } else {
                    reject(new Error('Failed to connect after multiple attempts.'));
                }
            };
            attempt(retries);
        });
    }

    stopExecution(id: string) {
        if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
            this.hubConnection.invoke("StopExecution", id.toString(), this.apiHeader['authorization'])
                .then(() => {
                    this.removeExecution(id);
                })
                .catch((err) => {
                    console.error(`Error invoking ExecutionError for ID: ${id}: `, err);
                    this.executionSuccessSubject.next({ id, msg: 'ExecutionError' }); // Notify failure with ID
                });
        } else {
            console.error('Hub connection is not active.');
        }
    }


    onReceiveProgress(callback: (progress: ManualProgressInfo) => void) {
        // Remove existing handler to prevent duplicates
        this.hubConnection.off('ReceiveProgress');
        this.hubConnection.on('ReceiveProgress', (progress) => {
            callback(progress);
        });
    }


    executeStoredProcedure(id: string, action: string) {
        if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
            this.hubConnection
                .invoke('StartExecution', id.toString(), action, this.apiHeader['authorization'])
                .then(() => {
                })
                .catch((err) => {
                    console.error(`Error invoking ExecutionError for ID: ${id}: `, err);
                    this.executionSuccessSubject.next({ id, msg: 'ExecutionError' }); // Notify failure with ID
                });
        } else {
            console.error('Hub connection is not active. Cannot execute stored procedure.');
            this.executionSuccessSubject.next({ id, msg: 'ExecutionError' });
        }
    }


    private updateActiveExecutions(execution: { id: string, action: string }) {
        const current = this.activeExecutions.getValue();
        this.activeExecutions.next([...current, execution]);
    }

    private removeExecution(id: string) {
        const current = this.activeExecutions.getValue().filter(e => e.id !== id);
        this.activeExecutions.next(current);
    }

    ngOnDestroy(): void {
        // Clean up connection on service destruction
        if (this.hubConnection) {
            this.hubConnection.stop()
                .then(() => console.log('SignalR connection stopped'))
                .catch((err) => console.error('Error stopping SignalR connection:', err));
        }
        this.executionSuccessSubject.complete();
        this.activeExecutions.complete();
    }
}

// Define an interface for ProgressInfo for better type safety
export class ManualProgressInfo {
    manualId: number;
    status: string;
    executionMaxTime: number;
    curentTime: number;
    isStop: number;
}